import React, { useState, useEffect } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../../firebase/firebaseConfig';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useTCoins } from '../../context/TCoinsContext';
import { useMarketplace } from '../../context/MarketplaceContext';
import UserProfileMenu from './UserProfileMenu';
import TCoinsWallet from '../TCoins/TCoinsWallet';
import NotificationBell from '../Notification/NotificationBell';
import SearchBar from './SearchBar';
import ProfileMenuDropdown from './ProfileMenuDropdown';
import MobileHamburgerMenu from './MobileHamburgerMenu';
import LanguageFlagButton from './LanguageFlagButton';
import { getFavoritesCount } from '../../utils/favoritesUtils';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import QuizBadges from '../Quiz/QuizBadges';

// FavoritesCountBadge Component
const FavoritesCountBadge = () => {
  const { currentUser } = useAuth();
  const [favoritesCount, setFavoritesCount] = useState(0);

  useEffect(() => {
    if (currentUser) {
      const updateCount = () => {
        const count = getFavoritesCount(currentUser.uid);
        setFavoritesCount(count);
      };
      
      updateCount();
      
      // Listen for favorites updates
      window.addEventListener('favoritesUpdated', updateCount);
      
      return () => {
        window.removeEventListener('favoritesUpdated', updateCount);
      };
    }
  }, [currentUser]);

  if (!currentUser || favoritesCount === 0) return null;

  return (
    <span className="absolute -top-1 -right-1 bg-white text-red-500 text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold border-2 border-red-500">
      {favoritesCount > 9 ? '9+' : favoritesCount}
    </span>
  );
};

const Navbar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [user] = useAuthState(auth);
  const { currentUser } = useAuth();
  const { getTotalTCoins } = useTCoins();
  const { cart } = useMarketplace();
  const [showTCoinsWallet, setShowTCoinsWallet] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [hasDailyBonus, setHasDailyBonus] = useState(false);
  const [quizUser, setQuizUser] = useState(null);

  // Listen for quiz user data updates
  useEffect(() => {
    const handleQuizUserUpdate = (event) => {
      setQuizUser(event.detail);
    };
    
    window.addEventListener('quizUserUpdate', handleQuizUserUpdate);
    
    return () => {
      window.removeEventListener('quizUserUpdate', handleQuizUserUpdate);
    };
  }, []);

  const isQuizPage = location.pathname.startsWith('/pharma-quiz') || location.pathname.startsWith('/mothai-quiz');

  // Listen for TCoinsWallet open event from ProfileMenuDropdown
  useEffect(() => {
    const handleOpenTCoinsWallet = () => {
      setShowTCoinsWallet(true);
      setShowProfileMenu(false); // Close profile menu when opening wallet
    };
    
    window.addEventListener('openTCoinsWallet', handleOpenTCoinsWallet);
    
    return () => {
      window.removeEventListener('openTCoinsWallet', handleOpenTCoinsWallet);
    };
  }, []);

  // Check daily bonus availability
  useEffect(() => {
    const checkDailyBonus = async () => {
      if (!currentUser) {
        setHasDailyBonus(false);
        return;
      }
      
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const userData = userSnap.data();
          const streakData = userData.dailyBonusStreak || {};
          const lastClaim = streakData.lastClaimDate?.toDate?.() || 
                          (streakData.lastClaimDate instanceof Date ? streakData.lastClaimDate : null);
          
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          // ตรวจสอบว่า claim วันนี้แล้วหรือยัง
          const claimedToday = lastClaim && 
            lastClaim.getDate() === today.getDate() &&
            lastClaim.getMonth() === today.getMonth() &&
            lastClaim.getFullYear() === today.getFullYear();
          
          setHasDailyBonus(!claimedToday);
        }
      } catch (error) {
        console.error('Error checking daily bonus:', error);
        setHasDailyBonus(false);
      }
    };
    
    checkDailyBonus();
    
    // Check every 5 minutes
    const interval = setInterval(checkDailyBonus, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [currentUser]);

  const CSS_VARS = {
    gold: '#D4AF37',
    goldLight: '#F4E4BA',
    goldDark: '#996515',
    greenDeep: '#1A3C34',
    greenForest: '#2D5A4A',
    greenHerb: '#4A7C59',
    cream: '#FDF8F0',
    white: '#FFFFFF',
  };

  return (
    <>
      <nav 
        className="text-white shadow-lg sticky top-0 z-40 backdrop-blur-md"
        style={{
          background: `linear-gradient(135deg, ${CSS_VARS.greenDeep} 0%, ${CSS_VARS.greenForest} 100%)`,
          borderBottom: `2px solid ${CSS_VARS.gold}33`
        }}
      >
        <div className="container mx-auto px-2 sm:px-4 py-2 sm:py-3">
          {/* Desktop Layout */}
          <div className="hidden md:flex items-center justify-between space-x-4">
            {/* Left: Logo + Quiz */}
            <div className="flex items-center space-x-4 flex-shrink-0">
              <Link to="/pharma-quiz" className="flex items-center space-x-2 hover:opacity-80 transition">
                <img 
                  src="/logo.png" 
                  alt="ติวหมอไทย Logo" 
                  className="w-8 h-8 object-contain"
                  onError={(e) => {
                    // Hide logo if not found, text will show
                    e.target.style.display = 'none';
                  }}
                />
                <span 
                  className="text-xl font-bold"
                  style={{ fontFamily: "'Kanit', sans-serif" }}
                >
                  ติวหมอไทย
                </span>
              </Link>
              <Link 
                to="/pharma-quiz" 
                className="px-3 py-1.5 rounded-lg transition flex items-center space-x-2 shadow-lg hover:shadow-xl transform hover:scale-105"
                style={{
                  background: `linear-gradient(135deg, ${CSS_VARS.gold} 0%, ${CSS_VARS.goldDark} 100%)`,
                  fontFamily: "'Kanit', sans-serif"
                }}
                onMouseEnter={(e) => {
                  e.target.style.opacity = '0.9';
                }}
                onMouseLeave={(e) => {
                  e.target.style.opacity = '1';
                }}
                title="ติวข้อสอบแพทย์แผนไทย"
              >
                <span>📚</span>
                <span className="hidden lg:inline">ติวข้อสอบ</span>
                <span className="lg:hidden">📚</span>
              </Link>
            </div>

            {/* Center: Language Flag + Search Bar */}
            {user && (
              <div className="flex-1 max-w-2xl flex items-center gap-2">
                {/* Language Flag Button */}
                <LanguageFlagButton />
                {/* Search Bar */}
                <div className="flex-1">
                  <SearchBar />
                </div>
              </div>
            )}

            {/* Right: Actions + Profile */}
            {user ? (
              <div className="flex items-center space-x-2 flex-shrink-0">
                {/* Quiz Badges (Level, XP, Streak) - Only on quiz pages */}
                {isQuizPage && <QuizBadges quizUser={quizUser} />}
                
                {/* T Coins Button */}
                <button
                  onClick={() => setShowTCoinsWallet(true)}
                  className="relative px-3 py-1.5 bg-yellow-500 rounded-lg hover:bg-yellow-600 transition flex items-center space-x-2"
                  title={`${getTotalTCoins()} T Coins`}
                >
                  <span>🪙</span>
                  <span className="hidden lg:inline">{getTotalTCoins()}</span>
                  {hasDailyBonus && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold animate-pulse">
                      🎁
                    </span>
                  )}
                </button>
                
                {/* Notification Bell */}
                <NotificationBell />
                
                {/* Favorites */}
                <Link 
                  to="/marketplace/favorites" 
                  className="relative px-3 py-1.5 bg-red-500 rounded-lg hover:bg-red-600 transition flex items-center space-x-2"
                  title={t('marketplace.favoriteProducts', { defaultValue: 'สินค้าโปรด' })}
                >
                  <span>❤️</span>
                  <span className="hidden lg:inline">{t('marketplace.favoriteProducts', { defaultValue: 'สินค้าโปรด' })}</span>
                  <FavoritesCountBadge />
                </Link>
                
                {/* Cart */}
                <Link 
                  to="/marketplace/cart" 
                  className="relative px-3 py-1.5 bg-pink-500 rounded-lg hover:bg-pink-600 transition flex items-center space-x-2"
                  title={t('nav.cart')}
                >
                  <span>🛒</span>
                  {cart && cart.length > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                      {cart.length > 9 ? '9+' : cart.length}
                    </span>
                  )}
                  <span className="hidden lg:inline">{t('nav.cart')}</span>
                </Link>
                
                {/* Profile Menu Button */}
                <div className="relative">
                  <button
                    onClick={() => setShowProfileMenu(!showProfileMenu)}
                    className="flex items-center space-x-2 px-3 py-1.5 bg-white bg-opacity-20 rounded-lg hover:bg-opacity-30 transition"
                  >
                    <div className="w-8 h-8 rounded-full bg-white bg-opacity-30 flex items-center justify-center">
                      <span className="text-sm font-bold">
                        {currentUser?.displayName?.charAt(0) || 'U'}
                      </span>
                    </div>
                    <span className="hidden lg:inline text-sm font-medium">
                      {currentUser?.displayName?.split(' ')[0] || 'Profile'}
                    </span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {showProfileMenu && (
                    <ProfileMenuDropdown onClose={() => setShowProfileMenu(false)} />
                  )}
                </div>
              </div>
            ) : (
              <div className="flex space-x-2">
                <Link 
                  to="/pharma-quiz" 
                  className="px-3 py-1.5 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg hover:from-purple-600 hover:to-pink-600 transition flex items-center space-x-1 shadow-md"
                  title="ติวข้อสอบแพทย์แผนไทย"
                >
                  <span>⭐</span>
                  <span className="hidden lg:inline">0 XP</span>
                </Link>
                <Link to="/login" className="px-4 py-1.5 bg-green-500 rounded-lg hover:bg-green-600 transition">
                  {t('common.login')}
                </Link>
                <Link to="/register" className="px-4 py-1.5 bg-blue-500 rounded-lg hover:bg-blue-600 transition">
                  {t('common.register')}
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Layout */}
          <div className="md:hidden flex items-center justify-between">
            {/* Left: Logo */}
            <Link to="/pharma-quiz" className="flex items-center space-x-2">
              <img 
                src="/logo.png" 
                alt="ติวหมอไทย Logo" 
                className="w-7 h-7 object-contain"
                onError={(e) => {
                  // Hide logo if not found, text will show
                  e.target.style.display = 'none';
                }}
              />
              <span className="text-lg font-bold">ติวหมอไทย</span>
            </Link>

            {/* Right: Actions */}
            {user ? (
              <div className="flex items-center space-x-2">
                {/* Language Flag Button */}
                <LanguageFlagButton />
                
                {/* Search Icon */}
                <button
                  onClick={() => navigate('/marketplace')}
                  className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition"
                  title={t('marketplace.searchPlaceholder')}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </button>
                
                {/* Notification Bell */}
                <NotificationBell />
                
                {/* Hamburger Menu */}
                <button
                  onClick={() => setShowMobileMenu(true)}
                  className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition"
                  title={t('common.menu')}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              </div>
            ) : (
              <div className="flex space-x-2">
                <Link 
                  to="/pharma-quiz" 
                  className="px-2 py-1 bg-gradient-to-r from-purple-500 to-pink-500 rounded hover:from-purple-600 hover:to-pink-600 transition text-sm flex items-center space-x-1"
                  title="ติวข้อสอบ"
                >
                  <span>⭐</span>
                  <span className="text-xs">0</span>
                </Link>
                <Link to="/login" className="px-3 py-1 bg-green-500 rounded hover:bg-green-600 transition text-sm">
                  {t('common.login')}
                </Link>
                <Link to="/register" className="px-3 py-1 bg-blue-500 rounded hover:bg-blue-600 transition text-sm">
                  {t('common.register')}
                </Link>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile Hamburger Menu */}
      <MobileHamburgerMenu isOpen={showMobileMenu} onClose={() => setShowMobileMenu(false)} />
      
      {/* T Coins Wallet Modal */}
      {showTCoinsWallet && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
          onClick={(e) => {
            // Close when clicking backdrop
            if (e.target === e.currentTarget) {
              setShowTCoinsWallet(false);
            }
          }}
        >
          <div 
            className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <TCoinsWallet onClose={() => setShowTCoinsWallet(false)} />
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;